import { Injectable } from '@angular/core';
import { IUser } from '../../interfaces/user.interface';
import { ApiService } from '../api/api.service';

const USER_STORAGE_KEY = 'rb-current-user';

interface ILoginResponse {
  user: IUser;
  mustChangePassword?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private sessionValidation?: Promise<boolean>;
  private passwordChangeRequired = false;

  constructor(private apiService: ApiService) {}

  async login(username: string, password: string): Promise<IUser> {
    const response = (await this.apiService.request('/auth/login', {
      identifier: username,
      password,
    })) as ILoginResponse;

    if (!response?.user?.role) {
      throw new Error('Credenciales incorrectas');
    }

    this.passwordChangeRequired = Boolean(
      response.mustChangePassword ?? response.user.mustChangePassword
    );
    this.setCurrentUser(response.user);
    this.sessionValidation = Promise.resolve(true);

    return response.user;
  }

  async requestPasswordReset(identifier: string): Promise<void> {
    await this.apiService.request('/auth/request-password-reset', {
      identifier,
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.apiService.request('/auth/reset-password', {
      token,
      newPassword,
    });
  }

  /**
   * True while the signed-in user must set a policy-compliant password before
   * using the rest of the app. Cleared by `changePassword()` (which also ends
   * the session, forcing a fresh login).
   */
  mustChangePassword(): boolean {
    return this.passwordChangeRequired;
  }

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    await this.apiService.request('/auth/change-password', {
      currentPassword,
      newPassword,
    });

    // The backend invalidates every session on success; drop local state so the
    // guard sends the user back to the login screen.
    this.passwordChangeRequired = false;
    localStorage.removeItem(USER_STORAGE_KEY);
    this.sessionValidation = undefined;
  }

  async logout(): Promise<void> {
    try {
      await this.apiService.request('/auth/logout', {});
    } finally {
      localStorage.removeItem(USER_STORAGE_KEY);
      this.sessionValidation = undefined;
      this.passwordChangeRequired = false;
    }
  }

  getCurrentUser(): IUser | null {
    const rawUser = localStorage.getItem(USER_STORAGE_KEY);
    if (!rawUser) {
      return null;
    }

    try {
      return JSON.parse(rawUser) as IUser;
    } catch {
      return null;
    }
  }

  isLoggedIn(): boolean {
    return !!this.getCurrentUser();
  }

  ensureSession(): Promise<boolean> {
    if (!this.sessionValidation) {
      this.sessionValidation = this.validateSession();
    }

    return this.sessionValidation;
  }

  /**
   * Re-fetches the authenticated user from the backend and refreshes the copy
   * held in local storage. Call after the current user edits their own profile
   * so views that read `getCurrentUser()` (e.g. the navbar avatar) pick up the
   * change without a full re-login.
   */
  async refreshCurrentUser(): Promise<IUser | null> {
    this.sessionValidation = this.validateSession();
    const valid = await this.sessionValidation;
    return valid ? this.getCurrentUser() : null;
  }

  getCurrentRole(): IUser['role'] | null {
    return this.getCurrentUser()?.role ?? null;
  }

  hasRole(roles: IUser['role'][]): boolean {
    const role = this.getCurrentRole();
    return !!role && roles.includes(role);
  }

  getDefaultRoute(): string {
    switch (this.getCurrentRole()) {
      case 'MetaReviewer':
        return '/config/whatsapp';
      case 'Auxiliar':
        return '/appointments';
      default:
        return '/dashboard';
    }
  }

  private setCurrentUser(user: IUser): void {
    const safeUser: IUser = {
      ...user,
      password: undefined,
    };

    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(safeUser));
  }

  private async validateSession(): Promise<boolean> {
    try {
      const response = (await this.apiService.request('/auth/me', {})) as {
        user?: IUser;
        mustChangePassword?: boolean;
      };

      if (!response?.user?.role) {
        throw new Error('Sesión inválida');
      }

      this.passwordChangeRequired = Boolean(
        response.mustChangePassword ?? response.user.mustChangePassword
      );
      this.setCurrentUser(response.user);
      return true;
    } catch {
      localStorage.removeItem(USER_STORAGE_KEY);
      return false;
    }
  }
}
